import React, { forwardRef, useEffect, useState } from 'react';
import { CustomModal, BuySellDetailsMRT, LimitBanner } from '@components';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    FormHelperText,
    OutlinedInput,
    Select,
    Typography,
    MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
    apiBuyContract,
    apiBuyPegnordContractMinMax,
    apiGetMinMaxRaw,
    apiGetOrder,
    apiGetPegnordChf,
    apiGetPegnordMinMaxDisplay
} from '@store';
import { showToast, getMargin } from '@utils';
import { useLimitCheck } from '@hooks';

export const Sell = forwardRef(
    ({ handleClose, selectedContract, user, sym }, ref) => {
        const dispatch = useDispatch();
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);

        const [state, setState] = useState({
            market: 'PEG',
            type: 'sell',
            seller: user?.CompanyShort,
            buyer: '',
            deliverypoint: 'PEG',
            DeliveryPoint: ['PEG'],
            volume: '',
            exitpoint: '',
            price_percent: '100',
            ordertype: 'internal',
            currency: 'CHF',
            sym: selectedContract?.contract || sym,
            max_price: '',
            on_max: false,
            min_price: '',
            on_min: false
        });

        const [margin, setMargin] = useState(0);
        const [submitting, setSubmitting] = useState(false);
        const [showVolumeError, setShowVolumeError] = useState(false);
        const [showMinMaxInput, setShowMinMaxInput] = useState(false);
        const [type, setType] = useState('bid');

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state.volume]);

        useEffect(() => {
            if (user?.CanSellTo?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    buyer: user.CanSellTo[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            if (
                name === 'volume' &&
                value !== '' &&
                !/^\d*\.?\d{0,2}$/.test(value)
            ) {
                return;
            }
            setState({
                ...state,
                [name]: value
            });
            if (name === 'volume') {
                const vol = parseFloat(value);
                setShowVolumeError(value !== '' && !(vol > 0));
            }
        };

        const handleTypeChange = (e) => {
            setType(e.target.value);
            if (e.target.value === 'min_max') {
                setShowMinMaxInput(true);
            } else if (e.target.value === 'bid') {
                setShowMinMaxInput(false);
            }
        };

        const handleMinChange = (e) => {
            const value = e.target.value;
            if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) {
                return;
            }
            if (e.target.value === '') {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: false,
                    on_max: false
                });
            } else {
                setState({
                    ...state,
                    min_price: e.target.value,
                    max_price: '',
                    on_min: true,
                    on_max: false
                });
            }
        };

        const handleMaxChange = (e) => {
            const value = e.target.value;
            if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) {
                return;
            }
            if (e.target.value === '') {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: false,
                    on_min: false
                });
            } else {
                setState({
                    ...state,
                    max_price: e.target.value,
                    min_price: '',
                    on_max: true,
                    on_min: false
                });
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            const volume = parseFloat(state.volume);
            if (!(volume > 0)) {
                setShowVolumeError(true);
                showToast('error', 'Volume must be greater than 0');
                setSubmitting(false);
                return;
            }
            if (type === 'bid') {
                const { min_price, max_price, on_min, on_max, ...rest } = state;
                const bid = parseFloat(selectedContract?.bid);

                const data = {
                    ...rest,
                    margin: margin,
                    total_price: margin + bid,
                    volume,
                    price: { bid: bid },
                    exitpoint: state.exitpoint || {}
                };

                const resp = await dispatch(apiBuyContract(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetOrder());
                    showToast('success', 'Sales Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            } else if (type === 'min_max') {
                const minprice = parseFloat(state.min_price);
                const maxprice = parseFloat(state.max_price);

                const data = {
                    ...state,
                    margin: margin,
                    total_price: parseFloat(selectedContract?.bid) + margin,
                    volume,
                    min_price: minprice || 0,
                    max_price: maxprice || 0,
                    on_min: minprice ? true : false,
                    on_max: maxprice ? true : false,
                    exitpoint: state.exitpoint || {}
                };

                const resp = await dispatch(apiBuyPegnordContractMinMax(data));
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    dispatch(apiGetMinMaxRaw());
                    dispatch(
                        apiGetPegnordMinMaxDisplay({
                            product: 'PEG',
                            currency: 'CHF'
                        })
                    );
                    dispatch(apiGetPegnordChf());
                    dispatch(apiGetOrder());
                    showToast('success', 'Sales Order Saved');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp?.payload || 'Error buying contract'
                    );
                }
            }
            setSubmitting(false);
        };

        // timer
        const [timer, setTimer] = useState(25);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((t) => t - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (ref?.current) {
                setTimer(25);
            }
        }, [ref]);

        const notionalValue = state?.volume
            ? selectedContract?.contract_hours *
              (parseFloat(selectedContract?.bid) - margin) *
              parseFloat(state.volume)
            : 0;

        const limitCheck = useLimitCheck({
            action: 'sell',
            currency: 'chf',
            notionalValue,
            volume: state.volume
        });

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                timer={timer}
                modalTitle={`Sell Contract`}
                modalTitleColor={'#EB6969'}
                buttonText={'Sell'}
                className="modal-button-danger"
                disabled={
                    submitting ||
                    !(parseFloat(state.volume) > 0) ||
                    limitCheck.isDisabled
                }
                ref={ref}
            >
                <Box className="flex flex-col gap-3">
                    <LimitBanner messages={limitCheck.messages} />
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Please Select what you would like to sell
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Type"
                            name="type"
                            value={type}
                            onChange={handleTypeChange}
                        >
                            <MenuItem value="bid">Bid Price</MenuItem>
                            <MenuItem value="min_max">Min/Max Stop</MenuItem>
                        </Select>
                    </FormControl>
                    <Box className="flex gap-3">
                        <Typography>Contract:</Typography>
                        <Typography color={'black'}>
                            {selectedContract?.contract || sym}
                        </Typography>
                    </Box>
                    <FormControl
                        fullWidth
                        variant="outlined"
                        error={showVolumeError}
                    >
                        <InputLabel
                            id="contractLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Volume
                        </InputLabel>
                        <OutlinedInput
                            id="volume"
                            label="Volume"
                            name="volume"
                            type="number"
                            value={state.volume}
                            onChange={handleChange}
                            inputProps={{ step: 0.01, min: 0 }}
                            required
                            endAdornment={
                                <InputAdornment position="start" sx={{ ml: 2 }}>
                                    MW
                                </InputAdornment>
                            }
                        />
                        <FormHelperText>
                            {showVolumeError
                                ? 'Volume must be greater than 0'
                                : ''}
                        </FormHelperText>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Endpoint
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Endpoint"
                            name="exitpoint"
                            value={state.exitpoint}
                            onChange={handleChange}
                        >
                            {user?.Endpoints?.map((endpoint, index) => (
                                <MenuItem key={index} value={endpoint}>
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Sell To
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Sell To"
                            name="buyer"
                            value={state?.buyer}
                            onChange={handleChange}
                        >
                            {user?.CanSellTo?.map((seller, index) => (
                                <MenuItem key={index} value={seller}>
                                    {seller}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth variant="outlined" disabled>
                        <InputLabel
                            id="deliverypointLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Delivery Point
                        </InputLabel>
                        <OutlinedInput
                            id="deliverypoint"
                            label="Delivery Point"
                            name="deliverypoint"
                            value={state.deliverypoint || 'PEG'}
                        />
                    </FormControl>
                    {showMinMaxInput && (
                        <Box className="flex gap-3">
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Max Price
                                </InputLabel>
                                <OutlinedInput
                                    id="max_price"
                                    label="Price"
                                    name="max_price"
                                    value={state.max_price}
                                    onChange={handleMaxChange}
                                    type="number"
                                    inputProps={{ step: 0.001, min: 0 }}
                                    disabled={state.on_min}
                                    required
                                />
                            </FormControl>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel
                                    id="priceLabel"
                                    sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                >
                                    Min Price
                                </InputLabel>
                                <OutlinedInput
                                    id="min_price"
                                    label="Price"
                                    name="min_price"
                                    value={state.min_price}
                                    onChange={handleMinChange}
                                    type="number"
                                    inputProps={{ step: 0.001, min: 0 }}
                                    disabled={state.on_max}
                                    required
                                />
                            </FormControl>
                        </Box>
                    )}
                    <BuySellDetailsMRT
                        price={selectedContract?.bid}
                        margin={margin || 0}
                        totalPrice={(
                            parseFloat(selectedContract?.bid) - margin
                        ).toFixed(3)}
                        currency="CHF"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={
                            state?.volume
                                ? (
                                      selectedContract?.contract_hours *
                                      (parseFloat(selectedContract?.bid) -
                                          margin) *
                                      parseFloat(state.volume)
                                  ).toFixed(3)
                                : 0
                        }
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                </Box>
            </CustomModal>
        );
    }
);

export default Sell;
